import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import Navbar from '../../components/Navbar';
import { useAuth } from '../../context/AuthContext';

const Weeks = () => {
  const { user } = useAuth();
  const [weeks, setWeeks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingWeek, setEditingWeek] = useState(null);
  const [weekNumber, setWeekNumber] = useState('');
  const [seasonYear, setSeasonYear] = useState(new Date().getFullYear().toString());
  const [deadline, setDeadline] = useState('');
  const [announcement, setAnnouncement] = useState('');
  const [games, setGames] = useState([{ team1: '', team2: '', spread: '', total: '', props: [] }]);
  const [bulkText, setBulkText] = useState('');

  useEffect(() => {
    fetchWeeks();
  }, []);

  const fetchWeeks = async () => {
    try {
      const timestamp = new Date().getTime();
      const response = await api.get(`/weeks?t=${timestamp}`);
      setWeeks(response.data);
    } catch (error) {
      console.error('Error fetching weeks:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (games.length === 0 || games.every(g => !g.team1 || !g.team2)) {
      alert('Please add at least one game');
      return;
    }

    if (!deadline) {
      alert('Please set a deadline');
      return;
    }

    const validGames = games.filter(g => g.team1 && g.team2).map(g => ({
      team1: g.team1,
      team2: g.team2,
      spread: g.spread || '',
      total: g.total || '',
      props: g.props || []
    }));

    // Convert the datetime-local value (which is HST) to ISO format
    // datetime-local gives us something like "2025-12-05T22:00"
    // We need to treat this as HST time and convert to UTC
    const formattedDeadline = deadline.includes('T') ? deadline : deadline;

    try {
      if (editingWeek) {
        await api.put(`/weeks/${editingWeek.id}`, {
          deadline: formattedDeadline,
          games: validGames,
        });
        alert('Week updated successfully!');
      } else {
        await api.post('/weeks', {
          week_number: parseInt(weekNumber),
          season_year: parseInt(seasonYear),
          deadline: formattedDeadline,
          announcement: announcement,
          games: validGames,
        });
        alert('Week created successfully!');
      }
      
      await fetchWeeks();
      setShowModal(false);
      resetForm();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to save week');
    }
  };

  const handleDelete = async (weekId) => {
    try {
      await api.delete(`/weeks/${weekId}`);
      await fetchWeeks();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to delete week');
    }
  };

  const parseBulkGames = () => {
    if (!bulkText.trim()) {
      alert('Please enter games in the bulk text area');
      return;
    }

    try {
      const lines = bulkText.trim().split('\n');
      const parsedGames = [];
      let currentGame = null;
      let pendingTeam = null; // For handling teams on separate lines

      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed) continue;

        // Replace tabs with spaces for processing
        const lineWithSpaces = trimmed.replace(/\t+/g, ' ');
        
        // Check if this is a game line with both teams (TEAM1 +X TEAM2 -X)
        const fullGameMatch = lineWithSpaces.match(/^([A-Z\s]+)\s+([+-]\d+)\s+([A-Z\s]+)\s+([+-]\d+)\s*$/i);
        if (fullGameMatch) {
          currentGame = {
            team1: fullGameMatch[1].trim(),
            team2: fullGameMatch[3].trim(),
            spread: fullGameMatch[2],
            total: '',
            props: []
          };
          parsedGames.push(currentGame);
          pendingTeam = null;
          continue;
        }
        
        // Check if this is a single team line (TEAM +X or TEAM -X)
        const singleTeamMatch = lineWithSpaces.match(/^([A-Z\s]+)\s+([+-]\d+)\s*$/i);
        if (singleTeamMatch) {
          if (pendingTeam) {
            // We have a pending team, so this is team2
            currentGame = {
              team1: pendingTeam.team,
              team2: singleTeamMatch[1].trim(),
              spread: pendingTeam.spread,
              total: '',
              props: []
            };
            parsedGames.push(currentGame);
            pendingTeam = null;
          } else {
            // This is team1, wait for team2
            pendingTeam = {
              team: singleTeamMatch[1].trim(),
              spread: singleTeamMatch[2]
            };
          }
          continue;
        }

        // Split by tab for prop lines
        const parts = trimmed.split(/\t+/);
        
        for (const part of parts) {
          const partTrimmed = part.trim();
          if (!partTrimmed) continue;

          // Check if this is a player prop line - supports both "pass yds" and just "pass"
          const propMatch = partTrimmed.match(/^(.+?)\s+(under|over)\s+(\d+(?:\.\d+)?)\s+(pass|rush|rec)(?:\s+yds)?$/i);
          if (propMatch && currentGame) {
            const player = propMatch[1].trim();
            const propType = propMatch[2].toLowerCase();
            const value = propMatch[3];
            const yardType = propMatch[4].toLowerCase() + ' yds';
            
            if (!currentGame.props) {
              currentGame.props = [];
            }
            
            let existingProp = currentGame.props.find(p => 
              p.player.toLowerCase() === player.toLowerCase() && 
              p.prop_type === yardType
            );
            
            if (!existingProp) {
              existingProp = { player, prop_type: yardType, under_value: '', over_value: '' };
              currentGame.props.push(existingProp);
            }
            
            if (propType === 'under') {
              existingProp.under_value = value;
            } else {
              existingProp.over_value = value;
            }
            continue;
          }

          // Check if this is a first half over/under line (e.g., "Under 22 1H Pts" or "Over 22 1H Pts")
          const firstHalfMatch = partTrimmed.match(/^(under|over)\s+(\d+(?:\.\d+)?)\s+1H\s+Pts$/i);
          if (firstHalfMatch && currentGame) {
            const propType = firstHalfMatch[1].toLowerCase();
            const value = firstHalfMatch[2];
            
            if (!currentGame.props) {
              currentGame.props = [];
            }
            
            let existingProp = currentGame.props.find(p => p.prop_type === '1H pts');
            
            if (!existingProp) {
              existingProp = { player: '1H', prop_type: '1H pts', under_value: '', over_value: '' };
              currentGame.props.push(existingProp);
            }
            
            if (propType === 'under') {
              existingProp.under_value = value;
            } else {
              existingProp.over_value = value;
            }
            continue;
          }

          // Check if this is a game total over/under line (e.g., "Under 46 Total pts" or "Over 46 Total pts")
          const totalPtsMatch = partTrimmed.match(/^(under|over)\s+(\d+(?:\.\d+)?)\s+Total\s+pts$/i);
          if (totalPtsMatch && currentGame) {
            currentGame.total = totalPtsMatch[2];
            continue;
          }

          // Check if this is a game total over/under line (supports "Pts" suffix) - legacy format
          const totalMatch = partTrimmed.match(/(?:under|over)\s*(\d+(?:\.\d+)?)\s*(?:pts)?$/i);
          if (totalMatch && currentGame && !partTrimmed.toLowerCase().includes('rush') && !partTrimmed.toLowerCase().includes('rec') && !partTrimmed.toLowerCase().includes('pass') && !partTrimmed.toLowerCase().includes('1h') && !partTrimmed.toLowerCase().includes('total')) {
            currentGame.total = totalMatch[1];
          }
        }
      }

      if (parsedGames.length > 0) {
        console.log('Parsed games:', JSON.stringify(parsedGames, null, 2));
        setGames(parsedGames);
        setBulkText('');
        // Count total props
        const totalProps = parsedGames.reduce((sum, g) => sum + (g.props?.length || 0), 0);
        alert(`Parsed ${parsedGames.length} games with ${totalProps} player props!`);
      } else {
        alert('Could not parse any games. Please check the format.');
      }
    } catch (error) {
      alert('Error parsing games: ' + error.message);
    }
  };

  const resetForm = () => {
    setWeekNumber('');
    setSeasonYear(new Date().getFullYear().toString());
    setDeadline('');
    setAnnouncement('');
    setGames([{ team1: '', team2: '', spread: '', total: '', props: [] }]);
    setBulkText('');
    setEditingWeek(null);
  };

  const openCreateModal = () => {
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (week) => {
    setEditingWeek(week);
    setWeekNumber(week.week_number.toString());
    setSeasonYear(week.season_year.toString());
    setDeadline(week.deadline);
    setGames(week.games.length > 0 ? week.games.map(g => ({...g, total: g.total || '', props: g.props || []})) : [{ team1: '', team2: '', spread: '', total: '', props: [] }]);
    setShowModal(true);
  };

  const addGame = () => {
    setGames([...games, { team1: '', team2: '', spread: '', total: '', props: [] }]);
  };

  const updateGame = (index, field, value) => {
    const newGames = [...games];
    newGames[index][field] = value;
    setGames(newGames);
  };

  const removeGame = (index) => {
    if (games.length > 1) {
      setGames(games.filter((_, i) => i !== index));
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-dark">
        <Navbar />
        <div className="flex items-center justify-center h-96">
          <div className="text-white text-xl">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark">
      <Navbar />
      
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-primary">Weeks Management</h1>
          <button
            onClick={openCreateModal}
            className="px-6 py-3 bg-primary text-white rounded-lg font-bold hover:bg-green-600 transition"
          >
            + Create Week
          </button>
        </div>

        {weeks.length === 0 ? (
          <div className="bg-dark-secondary rounded-lg p-6 text-center">
            <p className="text-gray-300 text-xl">No weeks created yet</p>
          </div>
        ) : (
          <div className="grid gap-4">
            {weeks.map((week) => {
              let touchStartX = 0;
              let touchEndX = 0;
              
              const handleTouchStart = (e) => {
                touchStartX = e.touches[0].clientX;
              };
              
              const handleTouchEnd = (e) => {
                touchEndX = e.changedTouches[0].clientX;
                const swipeDistance = touchStartX - touchEndX;
                
                if (swipeDistance > 100) {
                  handleDelete(week.id);
                }
              };

              return (
                <div 
                  key={week.id} 
                  className="bg-dark-secondary rounded-lg p-6 touch-pan-y"
                  onTouchStart={handleTouchStart}
                  onTouchEnd={handleTouchEnd}
                >
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h2 className="text-2xl font-bold text-white mb-1">
                        Week {week.week_number} - {week.season_year}
                      </h2>
                      <p className="text-gray-400">
                        Deadline: {new Date(week.deadline).toLocaleString('en-US', { timeZone: 'Pacific/Honolulu', dateStyle: 'short', timeStyle: 'short' })} HST
                      </p>
                      <p className="text-gray-500 text-sm">{week.games.length} games</p>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => openEditModal(week)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(week.id)}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
                      >
                        Delete
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    {week.games.map((game, index) => (
                      <div key={index} className="bg-dark rounded-lg p-3 flex items-center justify-between">
                        <span className="text-white">
                          {game.team1} <span className="text-gray-400 font-bold mx-2">vs</span> {game.team2}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-dark-secondary rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold text-white mb-6">
              {editingWeek ? 'Edit Week' : 'Create Week'}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-300 mb-2">Week Number</label>
                  <input
                    type="number"
                    value={weekNumber}
                    onChange={(e) => setWeekNumber(e.target.value)}
                    required
                    disabled={!!editingWeek}
                    className="w-full bg-dark text-white px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
                  />
                </div>

                <div>
                  <label className="block text-gray-300 mb-2">Season Year</label>
                  <input
                    type="number"
                    value={seasonYear}
                    onChange={(e) => setSeasonYear(e.target.value)}
                    required
                    disabled={!!editingWeek}
                    className="w-full bg-dark text-white px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
                  />
                </div>
              </div>

              <div>
                <label className="block text-gray-300 mb-2">Deadline (HST)</label>
                <input
                  type="datetime-local"
                  value={deadline}
                  onChange={(e) => setDeadline(e.target.value)}
                  required
                  className="w-full bg-dark text-white px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              <div>
                <label className="block text-gray-300 mb-2">Week Announcement (Optional)</label>
                <textarea
                  value={announcement}
                  onChange={(e) => setAnnouncement(e.target.value)}
                  placeholder="Enter any announcement for this week..."
                  rows="3"
                  className="w-full bg-dark text-white px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              <div>
                <label className="block text-gray-300 mb-2">Bulk Add Games (Quick Method)</label>
                <textarea
                  value={bulkText}
                  onChange={(e) => setBulkText(e.target.value)}
                  placeholder="Paste games here (one per line):
LA RAMS -10 CAROLINA +10
LAR/CAR under 47 LAR/CAR over 47
GREEN BAY -1 CHICAGO +1
GB/CHI under 46 GB/CHI over 46"
                  rows="8"
                  className="w-full bg-dark text-white px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary font-mono text-sm"
                />
                <button
                  type="button"
                  onClick={parseBulkGames}
                  className="mt-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition"
                >
                  Parse Bulk Games
                </button>
                <p className="text-gray-500 text-sm mt-2">
                  Format: TEAM1 SPREAD TEAM2 SPREAD, then over/under line below each game
                </p>
              </div>

              <div className="border-t border-gray-700 pt-4">
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-gray-300">Games ({games.length})</label>
                  <button
                    type="button"
                    onClick={addGame}
                    className="px-3 py-1 bg-primary text-white rounded hover:bg-green-600 transition text-sm"
                  >
                    + Add Game
                  </button>
                </div>
                
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {games.map((game, index) => (
                    <div key={index} className="bg-dark rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="flex-1 flex items-center gap-2">
                          <input
                            type="text"
                            placeholder="Team 1"
                            value={game.team1}
                            onChange={(e) => updateGame(index, 'team1', e.target.value)}
                            className="flex-1 bg-dark-secondary text-white px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-primary"
                          />
                          <span className="text-primary font-bold text-lg min-w-[50px] text-center">
                            {game.spread || '-'}
                          </span>
                        </div>
                        <span className="text-gray-400 font-bold">vs</span>
                        <input
                          type="text"
                          placeholder="Team 2"
                          value={game.team2}
                          onChange={(e) => updateGame(index, 'team2', e.target.value)}
                          className="flex-1 bg-dark-secondary text-white px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-primary"
                        />
                        {games.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeGame(index)}
                            className="bg-red-600 text-white px-3 py-2 rounded hover:bg-red-700 transition"
                          >
                            ×
                          </button>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-gray-400 text-sm">O/U Total:</span>
                        <input
                          type="text"
                          placeholder="e.g., 47"
                          value={game.total || ''}
                          onChange={(e) => updateGame(index, 'total', e.target.value)}
                          className="w-24 bg-dark-secondary text-white px-3 py-1 rounded focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 bg-primary text-white py-3 rounded-lg font-bold hover:bg-green-600 transition"
                >
                  {editingWeek ? 'Update Week' : 'Create Week'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 bg-gray-600 text-white py-3 rounded-lg font-bold hover:bg-gray-700 transition"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Weeks;
